import React from 'react';
import Table from "src/componets/Table";
import { useAppSelector } from "src/store/hooks";
import { ColumnsType } from "antd/es/table";
import { CsvImportInterface } from "src/store/callrecorder/csvImportsSlice";
import { selectProviderEntities } from "src/store/callrecorder/providersSlice";
import { formattedDate } from "src/helpers/DateHelpers";
import Pagination from "src/componets/Pagination";
import OptionalLink from "src/componets/OptionalLink";

import { Progress, Tag } from "antd";
import { CsvImportStatus } from "src/store/callrecorder/csvImportsSlice";

interface ListTableInterface {
  className?: string;
  limit: number;
}

const STATUS_TAG: Record<CsvImportStatus, { color: string; label: string }> = {
  pending:    { color: 'default',    label: 'Pending' },
  processing: { color: 'processing', label: 'Processing' },
  completed:  { color: 'success',    label: 'Completed' },
  failed:     { color: 'error',      label: 'Failed' },
};

const _ListTable = ({ className, limit }: ListTableInterface) => {
  const ids = useAppSelector(state => state.callrecorder.csvImports.ids);
  const records = useAppSelector(state => state.callrecorder.csvImports.entities);
  const count = useAppSelector(state => state.callrecorder.csvImports.count);
  const providers = useAppSelector(selectProviderEntities);
  const data  = ids ? ids.map(id => records[id]) : [];

  const renderStatus = (status: CsvImportStatus) => {
    const tag = STATUS_TAG[status] || STATUS_TAG.pending;
    return <Tag color={tag.color}>{tag.label}</Tag>;
  }

  const columns: ColumnsType<CsvImportInterface> = [
    {
      key: 'id',
      title: 'ID',
      render: (_, { id, url }) => <OptionalLink link={url}>#{id}</OptionalLink>,
      width: 40
    },

    {
      key: 'provider_id',
      title: 'Provider',
      render: (_, { provider_id }) => {
        const provider = providers[provider_id];
        return provider ? <small>{provider.name}</small> : <small>{`#${provider_id}`}</small>;
      },
      width: 40
    },

    {
      key: 'status',
      title: 'Status',
      render: (_, { status }) => renderStatus(status),
      width: 40
    },

    {
      key: 'progress',
      title: 'Progress',
      render: (_, { progress, status }) => (
        <Progress
          percent={progress || 0}
          size="small"
          status={status === 'completed' ? 'success' : status === 'failed' ? 'exception' : 'active'}
        />
      ),
      width: 220
    },

    {
      key: 'rows',
      title: 'Rows',
      render: (_, { processed_rows, total_rows }) => (
        <small>{processed_rows} / {total_rows}</small>
      ),
      width: 100
    },

    {
      key: 'error_rows',
      title: 'Errors',
      render: (_, { error_rows }) => {
        if (error_rows > 0) return <Tag color="error">{error_rows}</Tag>;
        return <small>{error_rows}</small>;
      },
      width: 40
    },

    {
      key: 'created_at',
      title: 'Created at',
      render: (_, { created_at }) => {
        return <small>{formattedDate(created_at).dateTime}</small>;
      },
      width: 125
    },
  ];

  return (
    <div className={className}>
      <Table
        rowKey='id'
        columns={columns}
        dataSource={data}
        search_keys={['provider_id']}
        sort_keys={['created_at', 'provider_id']}
      />

      <Pagination className='v-mt-10' total={count} size={limit} />
    </div>
  )
}

export default _ListTable;
